import { makeStyles } from "@mui/styles";
import React, { PropsWithChildren, useCallback, useEffect } from "react";
import QACheckbox from "../Design/Controls/QACheckbox";
import SettingsTextField from "../Settings/Old/Form/SettingsTextField";
import { VariableDefinition } from "./VariableDefinitionForm";

interface Props {
    shadowedVariables?: VariableDefinition[];
    variables: VariableDefinition[];
    disabled?: boolean;
    onValidate: (valid: boolean) => void;
    onUpdate: (name: string, value: string) => void;
    values: {
        [key: string]: string;
    };
    rowMode?: boolean;
}

const useStyles = makeStyles({
    variables: {
        display: "flex",
        flexDirection: "column"
    },
    variablesRowMode: {
        display: "flex",
        flexDirection: "row",
        gap: "0.5rem",
        flexGrow: 1
    },
    variableRow: {
        display: "flex",
        gap: "0.5rem",
        alignItems: "center",
        padding: "0.5rem",
        "&:nth-child(odd)": {
            backgroundColor: "#FAFAFA"
        },
        "&:nth-child(even)": {
            backgroundColor: "#F5F5F5"
        }
    },
    variableName: {
        width: "25%",
        flexShrink: 0
    },
    variableRequired: {
        color: "#FF0000",
        fontWeight: 500
    },
    variableField: {
        flexGrow: 1,
        margin: 0
    }
});

const SettingsForm: React.FC<PropsWithChildren<Props>> = props => {
    const classes = useStyles();

    const { rowMode, shadowedVariables, variables, disabled, onValidate, onUpdate, values } = props;

    const renderVariable = useCallback((variable: VariableDefinition) => {
        const shadowed = shadowedVariables?.find(sv => sv.name === variable.name && sv.type === variable.type && !(!sv.required && variable.required));

        if (shadowed && rowMode) {
            return null;
        }

        switch (variable.type) {
            case "BOOLEAN": {
                return (
                    <QACheckbox
                        label={shadowed ? "Überschrieben durch Szenario-Variable" : (rowMode ? variable.name : "")}
                        disabled={disabled || !!shadowed}
                        className={classes.variableField}
                        checked={values[variable.name] === "true"}
                        onChanged={value => onUpdate(variable.name, String(value))} />
                );
            }
            case "NUMBER":
            case "STRING":
            case "SECRET": {
                let label = "";
                if (shadowed) {
                    label = "Überschrieben durch Szenario-Variable";
                } else if (rowMode) {
                    label = variable.name + (variable.required ? "*" : "") + (variable.defaultValue ? ` (${variable.defaultValue})` : "");
                } else if (!values[variable.name]) {
                    label = variable.defaultValue ?? "";
                }
                return (
                    <SettingsTextField
                        label={label}
                        className={classes.variableField}
                        type={variable.type === "NUMBER" ? "number" : (variable.type === "SECRET" ? "password" : "text")}
                        disabled={disabled || !!shadowed}
                        usePlaceholder={!rowMode}
                        value={values[variable.name] ?? ""}
                        onChanged={value => onUpdate(variable.name, value)} />
                );
            }
        }
    }, [classes.variableField, disabled, onUpdate, rowMode, shadowedVariables, values]);

    useEffect(() => {
        onValidate(variables.every(v => v.required ? (!!values[v.name] || !!v.defaultValue) : true));
    }, [onValidate, values, variables]);

    if (rowMode) {
        return (
            <div className={classes.variablesRowMode}>
                {variables.map(v => renderVariable(v))}
            </div>
        );
    }

    return (
        <div className={classes.variables}>
            {variables.map(v => (
                <div className={classes.variableRow}>
                    <span className={classes.variableName}>
                        {v.name}
                        {v.required && (
                            <span className={classes.variableRequired}>*</span>
                        )}
                    </span>
                    {renderVariable(v)}
                </div>
            ))}
        </div>
    );
};

export default SettingsForm;
